import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, TabList, SectionCard, Kpi, CardSkeleton, EmptyState, StatusBadge, AutoBadge, NotConfigured, Modal, SecretRevealModal, DateCell } from "@/admin/ui";
import { ShieldCheck, KeyRound, Bell, Settings2, Activity, Siren, RefreshCw, AlertTriangle } from "lucide-react";

function TwoFaPanel() {
  const { t } = useLang();
  const [status, setStatus] = React.useState(null);
  const [setup, setSetup] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [secret, setSecret] = React.useState(null);
  const [codes, setCodes] = React.useState(null);
  const load = React.useCallback(async () => { setStatus((await api.get("/api/admin/2fa/status")).data); }, []);
  React.useEffect(() => { load(); }, [load]);
  const startSetup = async () => {
    const r = (await api.post("/api/admin/2fa/setup")).data;
    setSetup(r); setSecret({ value: r.secret_reveal_once, meta: `otpauth: ${r.otpauth_uri}` });
  };
  const confirm = async () => {
    try {
      const r = (await api.post("/api/admin/2fa/confirm", { code })).data;
      setCodes(r.recovery_codes_reveal_once);
      setSetup(null); setCode(""); load(); toast.success(t("adminp4.twofa.enabled"));
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  if (!status) return <CardSkeleton />;
  return (
    <div className="space-y-4">
      <SectionCard title={t("adminp4.twofa.title")} testid="p4-2fa"
        right={<StatusBadge tone={status.enabled ? "success" : "warning"}>{status.enabled ? "enabled" : "disabled"}</StatusBadge>}>
        <div className="text-white/70 text-sm">
          {status.enabled ? `${status.recovery_codes_remaining} recovery codes remaining` : "Not yet enabled for your admin account."}
        </div>
        {!status.enabled && !setup && <button className="lp-btn-primary text-xs" onClick={startSetup} data-testid="p4-2fa-setup">Start setup</button>}
        {setup && (
          <div className="space-y-2">
            <div className="text-white/60 text-xs">Secret was displayed in the reveal-once dialog. Now enter a 6-digit code from your authenticator app to confirm.</div>
            <input className="lp-input" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="p4-2fa-code" />
            <button className="lp-btn-primary text-xs" onClick={confirm} data-testid="p4-2fa-confirm">Confirm</button>
          </div>
        )}
        {codes && (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3">
            <div className="text-emerald-100 text-xs mb-2">Recovery codes (shown ONCE). Save them now.</div>
            <div className="grid grid-cols-2 gap-1">
              {codes.map((c) => <div key={c} className="font-mono text-white/95 text-xs">{c}</div>)}
            </div>
          </div>
        )}
      </SectionCard>
      <SecretRevealModal open={!!secret} secret={secret?.value || ""} meta={secret?.meta} title="TOTP setup key" onClose={() => setSecret(null)} />
    </div>
  );
}

function SecurityPanel() {
  const [dash, setDash] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const load = React.useCallback(async () => {
    const [d, e] = await Promise.all([
      api.get("/api/admin/security/dashboard").then((r) => r.data),
      api.get("/api/admin/security/events", { params: { per_page: 50 } }).then((r) => r.data),
    ]);
    setDash(d); setEvents(e.items);
  }, []);
  React.useEffect(() => { load(); }, [load]);
  if (!dash) return <CardSkeleton />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Active sessions" value={dash.sessions.active} testid="p4-sec-active" />
        <Kpi label="Suspicious" value={dash.sessions.suspicious} tone="warning" testid="p4-sec-susp" />
        <Kpi label="High risk" value={dash.sessions.high_risk} tone="danger" testid="p4-sec-high" />
        <Kpi label="Open events" value={dash.events.open} testid="p4-sec-open" />
      </div>
      <SectionCard title="Recent security events" testid="p4-sec-events"
        right={<button className="lp-btn-ghost" onClick={load} aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>}>
        {events.length === 0 ? <EmptyState title="No security events" /> : (
          <ul className="text-xs space-y-1">
            {events.map((e) => (
              <li key={e.id} className="flex justify-between border-t border-white/5 pt-1 gap-2">
                <span className="min-w-0 truncate">
                  <AutoBadge value={e.severity} /> <span className="text-white/70 ml-1">{e.category}</span> · <span className="text-white/85">{e.message}</span>
                </span>
                <span className="text-white/40 shrink-0"><DateCell value={e.created_at} /></span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function NotificationsPanel() {
  const [list, setList] = React.useState(null);
  const load = async () => setList((await api.get("/api/admin/notifications")).data);
  React.useEffect(() => { load(); }, []);
  if (!list) return <CardSkeleton />;
  const readAll = async () => { await api.post("/api/admin/notifications/mark-all-read"); load(); };
  return (
    <SectionCard title={`${list.unread} unread`} testid="p4-notif"
      right={<button className="lp-btn-secondary text-xs py-1 px-2" onClick={readAll}>Read all</button>}>
      {list.items.length === 0 ? <EmptyState title="No notifications" /> : (
        <ul className="text-xs space-y-1">
          {list.items.map((n) => (
            <li key={n.id} className={`flex justify-between border-t border-white/5 pt-1 gap-2 ${n.read ? "text-white/50" : "text-white/95"}`}>
              <span className="min-w-0 truncate"><AutoBadge value={n.severity} /> <span className="ml-1">{n.title}</span></span>
              <span className="text-white/40 shrink-0"><DateCell value={n.created_at} /></span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function SettingsPanel() {
  const [items, setItems] = React.useState(null);
  const load = async () => setItems((await api.get("/api/admin/settings-p4")).data.items);
  React.useEffect(() => { load(); }, []);
  const save = async (key, value) => {
    try { await api.put(`/api/admin/settings-p4/${key}`, { value }); toast.success("saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  if (!items) return <CardSkeleton />;
  const byGroup = {}; items.forEach((s) => { (byGroup[s.group] = byGroup[s.group] || []).push(s); });
  return (
    <div className="space-y-3">
      {Object.entries(byGroup).map(([g, arr]) => (
        <SectionCard key={g} title={g} testid={`p4-settings-${g}`}>
          <ul className="text-xs space-y-2 divide-y divide-white/5">
            {arr.map((s) => (
              <li key={s.key} className="flex items-center gap-3 flex-wrap pt-2 first:pt-0">
                <span className="text-white/60 min-w-[240px]">
                  {s.key}{s.critical && <StatusBadge tone="danger">critical</StatusBadge>}
                </span>
                {s.secret ? (
                  <span className="text-white/40">{s.is_set ? s.mask : "not set"} (secret — step-up required)</span>
                ) : s.type === "bool" ? (
                  <input type="checkbox" checked={!!s.value} onChange={(e) => save(s.key, e.target.checked)} data-testid={`p4-set-${s.key}`} />
                ) : s.type === "enum" ? (
                  <select className="lp-input text-xs py-1" defaultValue={s.value} onChange={(e) => save(s.key, e.target.value)} data-testid={`p4-set-${s.key}`}>
                    {s.enum.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input className="lp-input text-xs py-1 flex-1" defaultValue={s.value ?? ""}
                    onBlur={(e) => save(s.key, s.type === "int" || s.type === "duration_seconds" ? parseInt(e.target.value || "0") : e.target.value)}
                    data-testid={`p4-set-${s.key}`} />
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      ))}
    </div>
  );
}

function HealthPanel() {
  const [h, setH] = React.useState(null);
  const load = async () => setH((await api.get("/api/admin/health")).data);
  React.useEffect(() => { load(); }, []);
  if (!h) return <CardSkeleton />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <SectionCard title="Database" testid="p4-health-db">
        <StatusBadge tone={h.database.ok ? "success" : "danger"}>{h.database.ok ? "ok" : "down"}</StatusBadge>
        <div className="text-white/40 text-[10px] mt-1">{h.database.latency_ms ?? "—"}ms</div>
      </SectionCard>
      <SectionCard title="Email provider" testid="p4-health-email">
        {h.email_provider.configured ? <StatusBadge tone="success">{h.email_provider.provider}</StatusBadge>
          : <NotConfigured hint="Set RESEND_API_KEY in .env" />}
      </SectionCard>
      <SectionCard title="IP intelligence" testid="p4-health-ip">
        {h.ip_intelligence.configured ? <StatusBadge tone="success">configured</StatusBadge>
          : <NotConfigured hint="Set IPINFO_TOKEN in .env" />}
      </SectionCard>
      <SectionCard title="Creem" testid="p4-health-creem">
        {h.creem.configured ? <StatusBadge tone="success">configured</StatusBadge> : <NotConfigured />}
      </SectionCard>
      <SectionCard title="Webhooks 24h" testid="p4-health-hooks">
        <div className="text-white/85 text-xs">last: {h.webhooks_24h.last_received_at || "—"}</div>
        <div className="text-white/60 text-xs">failed: {h.webhooks_24h.failed_last_24h}</div>
      </SectionCard>
      <SectionCard title="API gateway 24h" testid="p4-health-api">
        <div className="text-white/85 text-xs tabular-nums">{h.api_gateway_24h.requests} req · {h.api_gateway_24h.errors} err</div>
      </SectionCard>
    </div>
  );
}

function IncidentsPanel() {
  const [items, setItems] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [sev, setSev] = React.useState("medium");
  const load = async () => setItems((await api.get("/api/admin/incidents-v2")).data.items);
  React.useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/api/admin/incidents-v2", { title, severity: sev }); toast.success("created"); setCreating(false); setTitle(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  if (!items) return <CardSkeleton />;
  return (
    <div className="space-y-3">
      <SectionCard title="Incidents" testid="p4-inc-list"
        right={<button className="lp-btn-primary text-xs py-1 px-2" onClick={() => setCreating(true)} data-testid="p4-inc-new">New</button>}>
        {items.length === 0 ? <EmptyState title="No incidents" /> : (
          <ul className="text-xs space-y-1">
            {items.map((i) => (
              <li key={i.id} className="border-t border-white/5 pt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <AutoBadge value={i.severity} />
                  <span className="text-white/90">{i.title}</span>
                </div>
                <div className="text-white/40 text-[10px]">{i.status} · owner {i.owner} · <DateCell value={i.started_at} /></div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
      <Modal open={creating} onClose={() => setCreating(false)} title="New incident"
        footer={<>
          <button className="lp-btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          <button className="lp-btn-primary" onClick={create} data-testid="p4-inc-submit" disabled={!title}>Create</button>
        </>}>
        <input className="lp-input w-full" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="p4-inc-title" data-autofocus />
        <select className="lp-input w-full" value={sev} onChange={(e) => setSev(e.target.value)} data-testid="p4-inc-sev">
          {["low", "medium", "high", "critical"].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </Modal>
    </div>
  );
}

export default function AdminSecurityOps() {
  const { t } = useLang();
  const [tab, setTab] = React.useState("twofa");
  const tabs = [
    { id: "twofa", icon: KeyRound, label: t("adminp4.tab.twofa") },
    { id: "security", icon: ShieldCheck, label: t("adminp4.tab.security") },
    { id: "notifications", icon: Bell, label: t("adminp4.tab.notifications") },
    { id: "settings", icon: Settings2, label: t("adminp4.tab.settings") },
    { id: "health", icon: Activity, label: t("adminp4.tab.health") },
    { id: "incidents", icon: Siren, label: t("adminp4.tab.incidents") },
  ];
  return (
    <div className="space-y-6" data-testid="admin-security-ops">
      <PageHeader title={t("adminp4.title")} description={t("adminp4.subtitle")} />
      <TabList value={tab} onChange={setTab} tabs={tabs} />
      {tab === "twofa" && <TwoFaPanel />}
      {tab === "security" && <SecurityPanel />}
      {tab === "notifications" && <NotificationsPanel />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "health" && <HealthPanel />}
      {tab === "incidents" && <IncidentsPanel />}
    </div>
  );
}
